import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Search, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import EntryOptionsSummary from '../trophy/EntryOptionsSummary';
import GenderChoice, { type EntryGender } from '../trophy/GenderChoice';
import { choosesTurn, chosenTurn } from '../trophy/entry-options';
import { useEntryOptions, type EntryOptionsFetcher } from '../trophy/useEntryOptions';
import {
  TeamEntryValidationError,
  teamEntriesService,
  type TeamEntry,
  type TeamEntryCandidate,
  type TeamEntryCandidates,
  type TeamReEntryInput,
} from '../../services/team-entries';

interface TeamReEntryDialogProps {
  open: boolean;
  competitionSlug: string;
  /** The squad of the card the dialog was opened from: whose athletes are searched. */
  team: { id: string; name: string } | null;
  onClose: () => void;
  onSaved: (entry: TeamEntry, created: boolean) => void;
  /** Nobody to pick: on to the empty form, for somebody new to the squad. */
  onNewAthlete: () => void;
}

/** What the team manager answers for a picked athlete: only what the earlier entry lacks. */
interface PickState {
  gender: EntryGender | null;
  birthDate: string;
  roundId: string | null;
  declaration: boolean;
}

type ErrorSlot = 'gender' | 'birthDate' | 'turn' | 'base';

const EMPTY_PICK: PickState = { gender: null, birthDate: '', roundId: null, declaration: false };

/** Which part of the form a server error belongs under. */
const SLOT_OF: Record<string, ErrorSlot> = {
  gender: 'gender',
  birth_date: 'birthDate',
  competition_round_id: 'turn',
  competition_round: 'turn',
};

/** Typing is let settle before the squad is searched again. */
const SEARCH_DELAY_MS = 250;

/**
 * "Enter an athlete", where the stage lets a squad enter again the athletes
 * who competed with it at the circuit's other stages: a **search** over them
 * first — by name or surname, or by an email address typed in full — and the
 * empty form only for somebody new ("Nuovo atleta").
 *
 * Each athlete found says where they last climbed with the squad and in which
 * class, and which of gender and date of birth their earlier entry lacks.
 * Picking one asks **only that**, shows the class and the turns the entry will
 * have (worked out by the server, from a date of birth this page never sees),
 * books the turn when the class has two or more, and wants the same
 * declaration as any squad entry. The server builds the row from the earlier
 * entry, address included: nothing here ever holds it.
 *
 * Failures stay in the dialog's state, and the translator is kept out of the
 * effects that load — the refetch-loop trap.
 */
export default function TeamReEntryDialog({
  open,
  competitionSlug,
  team,
  onClose,
  onSaved,
  onNewAthlete,
}: TeamReEntryDialogProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TeamEntryCandidates | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [picked, setPicked] = useState<TeamEntryCandidate | null>(null);
  const [form, setForm] = useState<PickState>(EMPTY_PICK);
  const [errors, setErrors] = useState<Partial<Record<ErrorSlot, string>>>({});
  const [saving, setSaving] = useState(false);
  const latestSearch = useRef(0);
  const teamId = team?.id ?? null;

  // A fresh search each time the dialog opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(null);
      setPicked(null);
      setForm(EMPTY_PICK);
      setErrors({});
    }
  }, [open]);

  // The squad's athletes, read again as the query changes; only the latest
  // answer counts.
  useEffect(() => {
    if (!open || !teamId) return;

    const request = ++latestSearch.current;
    const search = query.trim();
    setSearching(true);
    const timer = window.setTimeout(
      () => {
        teamEntriesService
          .candidates(competitionSlug, { search, teamId })
          .then((found) => {
            if (request !== latestSearch.current) return;
            setResults(found);
            setSearchFailed(false);
          })
          .catch(() => {
            if (request === latestSearch.current) setSearchFailed(true);
          })
          .finally(() => {
            if (request === latestSearch.current) setSearching(false);
          });
      },
      search ? SEARCH_DELAY_MS : 0
    );

    return () => window.clearTimeout(timer);
  }, [open, competitionSlug, teamId, query]);

  const asksGender = !!picked?.missing.includes('gender');
  const asksBirthDate = !!picked?.missing.includes('birth_date');
  const today = new Date().toISOString().slice(0, 10);
  const birthDate = form.birthDate.length === 10 && form.birthDate <= today ? form.birthDate : null;
  const answered = !!picked && (!asksGender || !!form.gender) && (!asksBirthDate || !!birthDate);

  // The preview is the server's: it holds the date of birth this page does
  // not. One fetcher per athlete picked, so the hook asks again only for them.
  const pickedId = picked?.id ?? null;
  const fetchOptions = useMemo<EntryOptionsFetcher>(
    () => (slug, details) =>
      teamEntriesService.entryOptions(slug, {
        from_entry_id: pickedId ?? '',
        gender: details.gender,
        birth_date: details.birth_date,
      }),
    [pickedId]
  );
  const { options, loading: optionsLoading, error: optionsError } = useEntryOptions(
    open && picked ? competitionSlug : null,
    answered ? { birthDate: asksBirthDate ? birthDate : null, gender: asksGender ? form.gender : null } : null,
    fetchOptions
  );
  const choosingTurn = choosesTurn(options);
  const chosenRound = chosenTurn(options, form.roundId);
  const turnsShown = !!options?.category && options.bookable_rounds.length > 0;

  const canSave = answered && form.declaration && !optionsLoading && (!choosingTurn || !!chosenRound);

  const set = (changes: Partial<PickState>) => setForm((current) => ({ ...current, ...changes }));

  const pick = (candidate: TeamEntryCandidate) => {
    setPicked(candidate);
    setForm(EMPTY_PICK);
    setErrors({});
  };

  const backToSearch = () => {
    setPicked(null);
    setErrors({});
  };

  const message = (code: string | undefined, detail: string) =>
    code ? t(`teamEntries.errors.${code}`, { defaultValue: detail }) : detail;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!picked || !canSave || saving) return;

    const input: TeamReEntryInput = {
      from_entry_id: picked.id,
      ...(asksGender ? { gender: form.gender } : {}),
      ...(asksBirthDate ? { birth_date: form.birthDate } : {}),
      ...(choosingTurn && chosenRound ? { competition_round_id: chosenRound } : {}),
    };

    setSaving(true);
    setErrors({});
    try {
      onSaved(await teamEntriesService.reEnter(competitionSlug, input, form.declaration), true);
    } catch (error) {
      if (error instanceof TeamEntryValidationError && Object.keys(error.fields).length > 0) {
        const next: Partial<Record<ErrorSlot, string>> = {};
        Object.entries(error.fields).forEach(([field, { code, detail }]) => {
          next[SLOT_OF[field] ?? 'base'] = message(code, detail);
        });
        setErrors(next);
      } else {
        const coded = error as { code?: string; message?: string };
        setErrors({ base: message(coded.code, coded.message ?? t('teamEntries.errors.generic')) });
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (slot: ErrorSlot) =>
    errors[slot] ? (
      <p className="text-xs text-destructive" data-testid={`team-reentry-error-${slot}`}>
        {errors[slot]}
      </p>
    ) : null;

  const lastStage = (candidate: TeamEntryCandidate) =>
    t('teamEntries.reEntry.lastStage', { team: candidate.team_name, stage: candidate.stage_name });

  const missingLine = (candidate: TeamEntryCandidate) =>
    t('teamEntries.reEntry.missing', {
      fields: candidate.missing.map((field) => t(`teamEntries.reEntry.missingFields.${field}`)).join(', '),
    });

  const candidates = results?.candidates ?? [];

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto overflow-x-hidden p-4 sm:max-w-lg sm:p-6">
        {!picked ? (
          <>
            <DialogHeader className="pr-6 text-left">
              <DialogTitle>{t('teamEntries.dialog.createTitle', { team: team?.name ?? '' })}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {t('teamEntries.reEntry.description', { team: team?.name ?? '' })}
              </DialogDescription>
            </DialogHeader>

            <div className="min-w-0 space-y-3" data-testid="team-reentry-search">
              <div className="space-y-1.5">
                <Label htmlFor="team-reentry-query">{t('teamEntries.reEntry.searchLabel')}</Label>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <Input
                    id="team-reentry-query"
                    type="search"
                    className="pl-8"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t('teamEntries.reEntry.searchPlaceholder')}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-muted-foreground">{t('teamEntries.reEntry.searchHint')}</p>
              </div>

              {searchFailed ? (
                <p className="text-sm text-destructive" data-testid="team-reentry-error">
                  {t('teamEntries.reEntry.loadError')}
                </p>
              ) : results === null ? (
                <div className="space-y-2" data-testid="team-reentry-loading">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : candidates.length === 0 ? (
                <p className="py-3 text-center text-sm text-muted-foreground" data-testid="team-reentry-empty">
                  {query.trim()
                    ? t('teamEntries.reEntry.noMatch')
                    : t('teamEntries.reEntry.empty', { team: team?.name ?? '' })}
                </p>
              ) : (
                <ul
                  className={`max-h-72 divide-y overflow-y-auto rounded-md border ${searching ? 'opacity-60' : ''}`}
                  data-testid="team-reentry-results"
                >
                  {candidates.map((candidate) => (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        className="flex w-full min-w-0 items-center gap-3 px-3 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                        onClick={() => pick(candidate)}
                        data-testid="team-reentry-candidate"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{candidate.full_name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {lastStage(candidate)}
                            {candidate.category_name && ` · ${candidate.category_name}`}
                          </span>
                          {candidate.missing.length > 0 && (
                            <span
                              className="block truncate text-xs text-amber-700 dark:text-amber-400"
                              data-testid="team-reentry-candidate-missing"
                            >
                              {missingLine(candidate)}
                            </span>
                          )}
                        </span>
                        <UserPlus className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {results && results.total > candidates.length && (
                <p className="text-xs text-muted-foreground" data-testid="team-reentry-truncated">
                  {t('teamEntries.reEntry.truncated', { shown: candidates.length, total: results.total })}
                </p>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('teamEntries.dialog.cancel')}
              </Button>
              <Button type="button" className="gap-2" onClick={onNewAthlete} data-testid="team-reentry-new">
                <UserPlus className="size-4" />
                {t('teamEntries.reEntry.newAthlete')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader className="pr-6 text-left">
              <DialogTitle>{t('teamEntries.reEntry.pickTitle', { name: picked.full_name })}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {t('teamEntries.reEntry.pickDescription', { team: picked.team_name, stage: picked.stage_name })}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="min-w-0 space-y-4" data-testid="team-reentry-form">
              {(asksGender || asksBirthDate) && (
                <p className="text-sm text-muted-foreground" data-testid="team-reentry-asks">
                  {t('teamEntries.reEntry.askMissing')}
                </p>
              )}

              {asksGender && (
                <fieldset className="space-y-1.5" data-testid="team-reentry-gender">
                  <legend className="text-sm font-medium">{t('teamEntries.dialog.gender')}</legend>
                  <GenderChoice name="team-reentry-gender" value={form.gender} onChange={(gender) => set({ gender })} />
                  <p className="text-xs text-muted-foreground">{t('stageEntry.dialog.genderHint')}</p>
                  {fieldError('gender')}
                </fieldset>
              )}

              {asksBirthDate && (
                <div className="space-y-1.5" data-testid="team-reentry-birth-date">
                  <Label htmlFor="team-reentry-birth-date">{t('teamEntries.dialog.birthDate')}</Label>
                  <Input
                    id="team-reentry-birth-date"
                    type="date"
                    max={today}
                    value={form.birthDate}
                    onChange={(event) => set({ birthDate: event.target.value })}
                    required
                  />
                  {fieldError('birthDate')}
                </div>
              )}

              <section className="min-w-0 space-y-2 rounded-lg border p-3" data-testid="team-reentry-options">
                {options ? (
                  <EntryOptionsSummary
                    options={options}
                    roundId={form.roundId}
                    onRoundChange={(roundId) => set({ roundId })}
                    turnError={errors.turn}
                    name="team-reentry-turn"
                    keptHint={t('teamEntries.dialog.categoryChosen')}
                  />
                ) : optionsLoading || (answered && optionsError === null) ? (
                  <div className="space-y-2" data-testid="team-reentry-options-loading">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : !answered ? (
                  <p className="text-sm text-muted-foreground" data-testid="team-reentry-options-pending">
                    {t('teamEntries.reEntry.optionsPending')}
                  </p>
                ) : null}
                {optionsError !== null && (
                  <p className="text-xs text-destructive" data-testid="team-reentry-options-error">
                    {optionsError || t('teamEntries.dialog.optionsError')}
                  </p>
                )}
                {errors.turn && !turnsShown && fieldError('turn')}
              </section>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="team-reentry-declaration"
                  checked={form.declaration}
                  onCheckedChange={(value) => set({ declaration: value === true })}
                  className="mt-0.5"
                />
                <Label htmlFor="team-reentry-declaration" className="text-sm font-normal leading-snug">
                  {t('teamEntries.dialog.declaration')}
                </Label>
              </div>

              {fieldError('base')}

              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" className="gap-2" onClick={backToSearch} disabled={saving}>
                  <ArrowLeft className="size-4" />
                  {t('teamEntries.reEntry.back')}
                </Button>
                <Button type="submit" disabled={!canSave || saving}>
                  {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                  {t('teamEntries.dialog.enter')}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
